import { Fragment, ReactNode } from 'react';
import { Link } from '@mui/material';
import type { LinkPreview } from './types';

// Global + capturing group so String.split keeps the URLs as delimiters.
const URL_SPLIT_PATTERN = /(https?:\/\/[^\s<>"')]+)/gi;
// Non-global, stateless check for "is this segment a URL?".
const IS_URL = /^https?:\/\//i;
const TRAILING_PUNCTUATION = /[.,);\]}'"]+$/;

/**
 * Format an ISO timestamp as a short relative time ("just now", "5m", "3h", "2d").
 * Falls back to a localized date for anything older than a week.
 */
export const formatRelativeTime = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const seconds = Math.round(diffMs / 1000);
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(then).toLocaleDateString();
};

/**
 * Render a comment body as React nodes, replacing any URL with a link that
 * shows the unfurled page title (falling back to the URL when no title was
 * resolved). Whitespace is preserved by the caller via `pre-wrap`.
 */
export const renderBodyWithLinks = (body: string, previews: LinkPreview[]): ReactNode[] => {
  const titleByUrl = new Map(previews.map((p) => [p.url, p.title]));
  const segments = body.split(URL_SPLIT_PATTERN);

  return segments.map((segment, index) => {
    if (!IS_URL.test(segment)) {
      return <Fragment key={index}>{segment}</Fragment>;
    }

    const cleanUrl = segment.replace(TRAILING_PUNCTUATION, '');
    const trailing = segment.slice(cleanUrl.length);
    const label = titleByUrl.get(cleanUrl) ?? cleanUrl;

    return (
      <Fragment key={index}>
        <Link href={cleanUrl} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-word' }}>
          {label}
        </Link>
        {trailing}
      </Fragment>
    );
  });
};
